/**
 * Recommendation API utilities
 * Handles communication with the integrated recommendation system
 */
import axios from 'axios';
import { ENV_CONFIG } from '../config/environment';

const API_BASE_URL = ENV_CONFIG.RECOMMENDATION_API_BASE;
const DEMO_PDF_IDS = new Set(['sample123']);
const KEYWORD_STOP_WORDS = new Set([
  'about', 'after', 'analysis', 'approach', 'based', 'between', 'could', 'data',
  'document', 'during', 'each', 'electronic', 'file', 'from', 'have', 'into',
  'journal', 'method', 'paper', 'papers', 'peer', 'results', 'review', 'show',
  'shows', 'study', 'system', 'that', 'their', 'these', 'this', 'through',
  'using', 'white', 'with', 'would'
]);
const PDF_TEXT_SLICE_LIMIT = 1400;
const ENDPOINT_UNAVAILABLE_TTL = 15 * 60 * 1000;
const ENDPOINT_UNAVAILABLE_KEY = 'recommendation_simple_endpoint_unavailable_until';

// Get authentication token
const getAuthToken = () => {
  return (
    localStorage.getItem('access_token') ||
    localStorage.getItem('wechat_token') ||
    localStorage.getItem('email_token') ||
    localStorage.getItem('authToken') ||
    null
  );
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 seconds
});

// Add auth interceptor
apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const extractKeywords = (text = '') => {
  if (!text) {
    return [];
  }

  const words = text.toLowerCase().match(/[a-zA-Z]{4,}/g) || [];
  return words.filter((word) => !KEYWORD_STOP_WORDS.has(word));
};

const buildProfileFromPayload = (userPdfData = []) => {
  const keywordCounts = new Map();

  userPdfData.forEach((pdf) => {
    const keywordSource = `${pdf.title || ''} ${pdf.content || ''}`;
    extractKeywords(keywordSource).forEach((keyword) => {
      keywordCounts.set(keyword, (keywordCounts.get(keyword) || 0) + 1);
    });
  });

  const topKeywords = [...keywordCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([keyword]) => keyword);

  return {
    based_on: topKeywords,
    user_profile: {
      user_id: 'local_profile',
      processed_pdfs: userPdfData.map((pdf) => pdf.title).filter(Boolean),
      top_keywords: topKeywords,
      papers_analyzed: userPdfData.length,
    },
  };
};

const isDemoPdf = (pdf = {}) => {
  const normalizedName = (pdf.name || pdf.title || pdf.filename || '').trim().toLowerCase();
  return DEMO_PDF_IDS.has(pdf.id) || normalizedName === 'white paper';
};

const mergePdfSources = (pdfs = []) => {
  const merged = new Map();

  pdfs.forEach((pdf, index) => {
    if (!pdf) {
      return;
    }

    const key = pdf.id || pdf.fileUrl || pdf.url || `${pdf.name || pdf.title || 'pdf'}-${index}`;
    const existing = merged.get(key) || {};
    merged.set(key, { ...existing, ...pdf });
  });

  return [...merged.values()];
};

const shouldSkipLiveRecommendationRequest = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const unavailableUntil = Number(sessionStorage.getItem(ENDPOINT_UNAVAILABLE_KEY) || 0);
  return unavailableUntil > Date.now();
};

const markLiveRecommendationEndpointUnavailable = () => {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem(
    ENDPOINT_UNAVAILABLE_KEY,
    String(Date.now() + ENDPOINT_UNAVAILABLE_TTL)
  );
};

const compactText = (value = '', maxLength = PDF_TEXT_SLICE_LIMIT) => {
  const normalized = String(value || '').replace(/\s+/g, ' ').trim();

  if (!normalized) {
    return '';
  }

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength)}...`;
};

const normalizePdfForRecommendations = (pdf, index) => {
  const contentParts = [
    pdf.name,
    pdf.title,
    pdf.author,
    pdf.authors,
    pdf.subject,
    pdf.keywords,
    pdf.journalName,
    compactText(pdf.text, 700),
    compactText(pdf.extractedText, 900),
    pdf.abstract,
  ].filter(Boolean).map((part) => compactText(part, 240));

  return {
    filename: String(pdf.name || pdf.filename || `PDF_${index + 1}`),
    title: String(pdf.name || pdf.title || pdf.subject || `PDF_${index + 1}`),
    content: compactText(contentParts.join(' '), PDF_TEXT_SLICE_LIMIT),
    fileUrl: String(pdf.url || pdf.fileUrl || ''),
    uploadTime: String(
      pdf.uploadTime ||
      pdf.timestamp ||
      pdf.creationDate ||
      pdf.lastViewed ||
      new Date().toISOString()
    ),
    id: String(pdf.id || index),
  };
};

const getRecentPdfsFromIndexedDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('PDFStore', 4);

    request.onerror = () => reject(new Error('Failed to open IndexedDB'));

    request.onsuccess = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains('recentPDFs')) {
        resolve([]);
        return;
      }

      const transaction = db.transaction(['recentPDFs'], 'readonly');
      const store = transaction.objectStore('recentPDFs');
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => resolve(getAllRequest.result || []);
      getAllRequest.onerror = () => reject(new Error('Failed to retrieve PDF data'));
    };
  });
};

/**
 * Track PDF reading behavior
 */
export const trackPDFReading = async (pdfData) => {
  try {
    const trackingData = {
      filename: pdfData.filename || pdfData.name || 'Unknown',
      title: pdfData.title || extractTitleFromFilename(pdfData.filename || pdfData.name),
      content_excerpt: pdfData.extractedText || pdfData.content || '',
      reading_duration: pdfData.readingTime || 0,
      pages_read: pdfData.pagesRead || 0
    };

    console.log('📚 Tracking PDF reading:', trackingData.title);

    const response = await apiClient.post('/track', trackingData);

    console.log('✅ PDF tracking successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ PDF tracking failed:', error.response?.data || error.message);
    // Don't throw error - tracking failure shouldn't break the app
    return null;
  }
};

/**
 * Access IndexedDB to get user's recent PDF data
 */
const getUserPDFDataFromIndexedDB = async (recentPdfs = []) => {
  try {
    const indexedDbPdfs = await getRecentPdfsFromIndexedDB();
    const mergedPdfs = mergePdfSources([...recentPdfs, ...indexedDbPdfs]);
    const filteredPdfs = mergedPdfs.filter((pdf) => !isDemoPdf(pdf));

    console.log(`📚 Found ${mergedPdfs.length} PDFs across app state and IndexedDB`);
    console.log(`🧠 Using ${filteredPdfs.length} real PDFs for personalized recommendations`);

    const transformedData = filteredPdfs.map((pdf, index) => {
      const result = normalizePdfForRecommendations(pdf, index);

      console.log(`📄 PDF ${index + 1} "${result.title}":`, {
        filename: result.filename,
        title: result.title,
        contentLength: result.content.length,
        contentPreview: result.content.substring(0, 100) + '...',
      });

      return result;
    });

    return transformedData;
  } catch (error) {
    console.error('❌ Error accessing IndexedDB:', error);
    return mergePdfSources(recentPdfs)
      .filter((pdf) => !isDemoPdf(pdf))
      .map((pdf, index) => normalizePdfForRecommendations(pdf, index));
  }
};

/**
 * Get personalized recommendations using Simple Algorithm (Step 1)
 */
export const getRecommendations = async (maxRecommendations = 6, options = {}) => {
  try {
    console.log('🎯 Step 1: Fetching personalized recommendations with user PDF data...');

    // Get user's PDF data from IndexedDB
    const userPdfData = await getUserPDFDataFromIndexedDB(options.recentPdfs || []);
    const localProfile = buildProfileFromPayload(userPdfData);

    if (userPdfData.length === 0) {
      console.log('📭 No real PDF data found, skipping live recommendations');
      return {
        recommendations: [],
        total_found: 0,
        based_on: [],
        user_profile: {
          ...localProfile.user_profile,
          status: 'warming_up',
        },
        search_source: 'Profile warm-up',
      };
    }

    console.log(`📊 Sending ${userPdfData.length} PDFs to recommendation engine`);

    if (shouldSkipLiveRecommendationRequest()) {
      console.log('⏭️ Skipping live recommendation request because the endpoint was recently unavailable');
      return {
        recommendations: [],
        total_found: 0,
        based_on: localProfile.based_on,
        user_profile: {
          ...localProfile.user_profile,
          status: 'backend_unavailable',
        },
        search_source: 'Local profile',
        error: 'Recommendation endpoint unavailable',
      };
    }

    // Send PDF data to our new simple recommendation endpoint
    const response = await apiClient.post('/simple', {
      user_pdf_data: userPdfData,
      max_recommendations: maxRecommendations,
      force_refresh: Boolean(options.forceRefresh),
    });

    console.log('✅ Smart recommendations fetched:', response.data.recommendations.length, 'papers');
    console.log('🔑 Based on keywords:', response.data.based_on);

    return {
      ...response.data,
      based_on: response.data.based_on?.length ? response.data.based_on : localProfile.based_on,
      user_profile: {
        ...localProfile.user_profile,
        ...response.data.user_profile,
      },
    };
  } catch (error) {
    console.error('❌ Failed to fetch recommendations:', error.response?.data || error.message);
    if (error.response?.status === 404) {
      markLiveRecommendationEndpointUnavailable();
    }
    const localProfile = buildProfileFromPayload(
      await getUserPDFDataFromIndexedDB(options.recentPdfs || [])
    );

    return {
      recommendations: [],
      total_found: 0,
      based_on: localProfile.based_on,
      user_profile: {
        ...localProfile.user_profile,
        status: 'backend_unavailable',
      },
      search_source: 'Local profile',
      error: error.response?.data?.detail || error.message,
    };
  }
};

/**
 * Get user profile and interests
 */
export const getUserProfile = async () => {
  try {
    const response = await apiClient.get('/profile');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to fetch user profile:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Simulate a reading session (for development/testing)
 */
export const simulateReadingSession = async () => {
  try {
    const response = await apiClient.post('/dev/simulate-reading');
    console.log('🧪 Simulated reading session:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to simulate reading session:', error.response?.data || error.message);
    return null;
  }
};

/**
 * Extract title from filename
 */
const extractTitleFromFilename = (filename) => {
  if (!filename) return 'Untitled Document';

  return filename
    .replace(/\.[^/.]+$/, '') // Remove extension
    .replace(/[_-]/g, ' ')    // Replace underscores and dashes with spaces
    .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
};

/**
 * PDF Reading Session Tracker
 * Tracks user's reading behavior automatically
 */
export class PDFReadingTracker {
  constructor(pdfData) {
    this.pdfData = pdfData;
    this.startTime = Date.now();
    this.endTime = null;
    this.pagesViewed = new Set();
    this.totalScrollDistance = 0;
    this.isTracking = true;

    console.log('📖 Started tracking PDF:', pdfData.filename || pdfData.name);
  }

  /**
   * Record that user viewed a specific page
   */
  viewPage(pageNumber) {
    if (this.isTracking) {
      this.pagesViewed.add(pageNumber);
    }
  }

  /**
   * Record scroll activity (for engagement tracking)
   */
  recordScroll(scrollDistance) {
    if (this.isTracking) {
      this.totalScrollDistance += scrollDistance;
    }
  }

  /**
   * End tracking session and send data to backend
   */
  async endSession() {
    if (!this.isTracking) return null;

    this.endTime = Date.now();
    this.isTracking = false;

    const readingDuration = Math.floor((this.endTime - this.startTime) / 1000); // in seconds
    const pagesRead = this.pagesViewed.size;

    const trackingData = {
      ...this.pdfData,
      readingTime: readingDuration,
      pagesRead: pagesRead,
      engagement: this.calculateEngagement()
    };

    console.log('📊 Ending PDF session:', {
      duration: `${readingDuration}s`,
      pages: pagesRead,
      engagement: trackingData.engagement
    });

    return await trackPDFReading(trackingData);
  }

  /**
   * Calculate engagement score
   */
  calculateEngagement() {
    const duration = (this.endTime - this.startTime) / 1000;
    const pagesRead = this.pagesViewed.size;

    // Simple engagement calculation
    let engagement = 0;

    if (duration > 30) engagement += 0.3;  // Spent at least 30 seconds
    if (duration > 300) engagement += 0.2; // Spent at least 5 minutes
    if (pagesRead > 3) engagement += 0.3;  // Read more than 3 pages
    if (this.totalScrollDistance > 1000) engagement += 0.2; // Active scrolling

    return Math.min(engagement, 1.0);
  }

  /**
   * Auto-end session after inactivity
   */
  startInactivityTimer(timeoutMs = 600000) { // 10 minutes default
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    this.inactivityTimer = setTimeout(() => {
      console.log('⏰ Auto-ending PDF session due to inactivity');
      this.endSession();
    }, timeoutMs);
  }

  /**
   * Reset inactivity timer (call on user activity)
   */
  resetInactivityTimer() {
    this.startInactivityTimer();
  }
}

const recommendationAPI = {
  trackPDFReading,
  getRecommendations,
  getUserProfile,
  simulateReadingSession,
  PDFReadingTracker
};

export default recommendationAPI;
